import React, { Suspense, lazy, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { ShareProvider, useShare } from './context/ShareContext';
import { CommunityProvider } from './context/CommunityContext';
import supabase from './supabase/supabase';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import FloatingChat from './components/FloatingChat';
import CookieConsent from './components/layout/CookieConsent';
import PwaManager from './components/PwaManager';
import AnalyticsTracker from './components/AnalyticsTracker';
import RouteMetaPolicy from './components/RouteMetaPolicy';
import MenuSourceHealthBanner from './components/admin/MenuSourceHealthBanner';
import Seo from './components/Seo';
import ListingDetail from './pages/ListingDetail';
import ProviderProfile from './pages/ProviderProfile';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import './App.css';

const Home = lazy(() => import('./pages/Home'));
const Feed = lazy(() => import('./pages/Feed'));
const AddFood = lazy(() => import('./pages/AddFood'));
const EditFood = lazy(() => import('./pages/EditFood'));
const Auth = lazy(() => import('./pages/Auth'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const PrivacyCenter = lazy(() => import('./pages/PrivacyCenter'));
const Chat = lazy(() => import('./pages/Chat'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const CompleteProfile = lazy(() => import('./pages/CompleteProfile'));
const Brochures = lazy(() => import('./pages/Brochures'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminMenuSources = lazy(() => import('./pages/AdminMenuSources'));
const Media = lazy(() => import('./pages/Media'));
const Saved = lazy(() => import('./pages/Saved'));
const MapFeed = lazy(() => import('./pages/MapFeed'));
const Notifications = lazy(() => import('./pages/Notifications'));
const ModerationQueue = lazy(() => import('./pages/ModerationQueue'));
const ShareTarget = lazy(() => import('./pages/ShareTarget'));

const Impressum = lazy(() => import('./pages/legal/Impressum'));
const Datenschutz = lazy(() => import('./pages/legal/Datenschutz'));
const Nutzungsbedingungen = lazy(() => import('./pages/legal/Nutzungsbedingungen'));
const HygieneRichtlinien = lazy(() => import('./pages/legal/HygieneRichtlinien'));

const AuthRecoveryRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const redirectToReset = () => navigate('/reset-password', { replace: true });
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (hash.get('type') === 'recovery') redirectToReset();

    const { data } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') redirectToReset();
    });

    return () => data.subscription.unsubscribe();
  }, [navigate]);

  return null;
};

const ScrollToTop = () => {
  const { pathname, search, hash } = useLocation();

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';

    const scrollToDestination = () => {
      if (hash) {
        const target = document.getElementById(decodeURIComponent(hash.slice(1)));
        if (target) {
          target.scrollIntoView({ block: 'start' });
          return;
        }
      }

      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollToDestination();
    const animationFrame = window.requestAnimationFrame(scrollToDestination);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [pathname, search, hash]);

  return null;
};

const NotFound = () => (
  <>
    <Seo title="Seite nicht gefunden" description="Die angeforderte Seite wurde nicht gefunden." noindex />
    <div className="min-h-[60vh] flex items-center justify-center bg-gray-50 px-4 py-12">
      <div className="max-w-md text-center bg-white border border-gray-100 rounded-2xl p-8 shadow-sm">
        <SafeIcon icon={FiIcons.FiCompass} className="text-5xl text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Seite nicht gefunden</h1>
        <p className="text-gray-600 mb-6">Der Link ist nicht mehr gültig oder die Seite wurde verschoben.</p>
        <Link to="/feed" className="inline-flex bg-emerald-600 text-white px-5 py-3 rounded-xl font-semibold">Zur Übersicht</Link>
      </div>
    </div>
  </>
);

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Fehler beim Anzeigen der Route:', error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    const message = this.state.error?.message || 'Unbekannter Darstellungsfehler';

    return (
      <div className="min-h-[60vh] bg-gray-50 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg bg-white border border-red-100 rounded-2xl shadow-sm p-7 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Die Seite konnte nicht angezeigt werden</h1>
          <p className="text-gray-600 mb-4">Der Fehler wurde abgefangen. Lade die aktuelle Version der Seite neu.</p>
          <pre className="text-left text-xs whitespace-pre-wrap break-words bg-gray-50 border rounded-xl p-3 mb-5 text-red-700">{message}</pre>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button type="button" onClick={() => window.location.reload()} className="bg-emerald-600 text-white px-5 py-3 rounded-xl font-semibold">Seite neu laden</button>
            <button type="button" onClick={() => { window.location.href = '/feed'; }} className="bg-gray-100 text-gray-800 px-5 py-3 rounded-xl font-semibold">Zur Übersicht</button>
          </div>
        </div>
      </div>
    );
  }
}

const RouteErrorBoundaryWithLocation = ({ children }) => {
  const location = useLocation();
  return <RouteErrorBoundary key={`${location.pathname}${location.search}`}>{children}</RouteErrorBoundary>;
};

const AuthenticatedGuard = ({ children }) => {
  const { user, loading } = useShare();
  if (loading) return <div className="p-20 text-center font-bold text-emerald-600">SchleiShare lädt...</div>;
  if (!user) return <Navigate to="/auth" />;
  return children;
};

const ProfileGuard = ({ children }) => {
  const { user, loading } = useShare();
  if (loading) return <div className="p-20 text-center font-bold text-emerald-600">SchleiShare lädt...</div>;
  if (!user) return <Navigate to="/auth" />;

  const isProfileComplete = user.name && user.address && user.name.trim() !== '' && user.address.trim() !== '';
  if (!isProfileComplete) return <Navigate to="/complete-profile" />;
  return children;
};

const AdminGuard = ({ children, adminOnly = false }) => {
  const { user, loading } = useShare();
  if (loading) return <div className="p-20 text-center font-bold text-emerald-600">SchleiShare lädt...</div>;
  if (!user) return <Navigate to="/auth" />;
  if (adminOnly ? user.role !== 'admin' : !['admin', 'moderator'].includes(user.role)) return <Navigate to="/feed" replace />;
  return children;
};

const AppContent = () => (
  <div className="min-h-screen bg-gray-50 flex flex-col">
    <AuthRecoveryRedirect />
    <AnalyticsTracker />
    <RouteMetaPolicy />
    <ScrollToTop />
    <Navbar />
    <main className="flex-grow" data-analytics-area="main-content">
      <MenuSourceHealthBanner />
      <RouteErrorBoundaryWithLocation>
        <Suspense fallback={<div className="p-20 text-center text-emerald-600 animate-pulse font-bold">Lade Seite...</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/feed" element={<Feed />} />
            <Route path="/map" element={<MapFeed />} />
            <Route path="/listing/:id" element={<ListingDetail />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/brochures" element={<Brochures />} />
            <Route path="/provider/:id" element={<ProviderProfile />} />
            <Route path="/media" element={<Media />} />
            <Route path="/share-target" element={<ShareTarget />} />

            <Route path="/profile" element={<ProfileGuard><Profile /></ProfileGuard>} />
            <Route path="/profile/:userId" element={<ProfileGuard><Profile /></ProfileGuard>} />
            <Route path="/datenschutz-center" element={<AuthenticatedGuard><PrivacyCenter /></AuthenticatedGuard>} />
            <Route path="/saved" element={<ProfileGuard><Saved /></ProfileGuard>} />
            <Route path="/notifications" element={<ProfileGuard><Notifications /></ProfileGuard>} />
            <Route path="/add" element={<ProfileGuard><AddFood /></ProfileGuard>} />
            <Route path="/edit/:id" element={<ProfileGuard><EditFood /></ProfileGuard>} />
            <Route path="/chat" element={<ProfileGuard><Chat /></ProfileGuard>} />
            <Route path="/chat/:chatId" element={<ProfileGuard><Chat /></ProfileGuard>} />
            <Route path="/complete-profile" element={<CompleteProfile />} />
            <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
            <Route path="/admin/users" element={<AdminGuard adminOnly><AdminUsers /></AdminGuard>} />
            <Route path="/admin/menu-sources" element={<AdminGuard><AdminMenuSources /></AdminGuard>} />
            <Route path="/admin/mail" element={<AdminGuard adminOnly><Navigate to="/admin" replace /></AdminGuard>} />
            <Route path="/admin/moderation" element={<AdminGuard><ModerationQueue /></AdminGuard>} />

            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/nutzungsbedingungen" element={<Nutzungsbedingungen />} />
            <Route path="/hygiene-richtlinien" element={<HygieneRichtlinien />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundaryWithLocation>
    </main>
    <Footer />
    <FloatingChat />
    <CookieConsent />
    <PwaManager />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <ShareProvider>
        <CommunityProvider>
          <AppContent />
        </CommunityProvider>
      </ShareProvider>
    </BrowserRouter>
  );
}

export default App;
